<template>
  <div
      class="widget-container"
      :style="widgetStyle"
  >
    <component
        :is="resolvedComponent"
        v-bind="resolvedProps"
        v-on="events"
    />

  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import DashboardWidget, {DashboardWidgetWidgetTypes, WidgetInfo} from "@/generated/classes/DashboardWidget.ts";
import KpiWidget from "@/framework/dashboard/components/KpiWidget.vue";
import DoughnutChart from "@/framework/chart/components/DoughnutChart2.vue";
import BarChart from "@/framework/chart/components/BarChart2.vue";
import MultiTabWidget from "@/framework/dashboard/components/MultiTabWidget.vue";

export default defineComponent({
  name: 'WidgetRenderer',

  components: {
    KpiWidget,
    DoughnutChart,
    BarChart,
    MultiTabWidget
  },

  props: {
    widget: {
      type: Object,
      required: true
    }
  },

  computed: {
    resolvedComponent() {
      switch (this.widget.widgetType) {
        case DashboardWidgetWidgetTypes.KPI:
          return KpiWidget

        case DashboardWidgetWidgetTypes.DOUGHNUT:
          return DoughnutChart

        case DashboardWidgetWidgetTypes.BAR:
          return BarChart

        case DashboardWidgetWidgetTypes.MULTITAB:
          return MultiTabWidget
        default:
          return null
      }
    },

    resolvedProps() {
      const w = this.widget

      switch (w.widgetType) {
        case DashboardWidgetWidgetTypes.KPI:
          return {
            id: w.id,
            title: w.title,
            value: w.data?.value,
            btnLabel: w.widgetMeta?.buttonLabel,
            emptyText: w.widgetMeta?.emptyText,
          }

        case DashboardWidgetWidgetTypes.DOUGHNUT:
          return {
            id: w.id,
            title: w.title,
            labels: Array.isArray(w.data?.labels) ? [...w.data.labels] : [],
            data: Array.isArray(w.data?.values) ? [...w.data.values] : [],
            chartOptions: w.widgetMeta?.chartOptions || {},
            emptyText: w.widgetMeta?.emptyText,
          }

        case DashboardWidgetWidgetTypes.BAR:
          return {
            id: w.id,
            title: w.title,
            labels: Array.isArray(w.data?.labels) ? [...w.data.labels] : [],
            data: Array.isArray(w.data?.values) ? [...w.data.values] : [],
            chartOptions: w.widgetMeta?.chartOptions || {}
          }

        case DashboardWidgetWidgetTypes.MULTITAB:
          return {
            widget: w
          }

        default:
          return {widget: w}
      }
    },

    widgetStyle() {
      const meta = this.widget.widgetMeta || {}

      return {
        gridColumn: `${meta.col} / span ${meta.colSpan}`,
        gridRow: `${meta.row} / span ${meta.rowSpan}`,
        order: meta.order || 0
      }
    },

    events(): Record<string, () => void> {
      const event: any = {};
      event["widgetAction"] = (widgetInfo: WidgetInfo) => this.widgetAction(widgetInfo);
      event["select"] = (widgetInfo: WidgetInfo) => this.selectionChanged(widgetInfo);

      return event;
    },
  },
  methods: {
    widgetAction(widgetInfo: WidgetInfo) {
      DashboardWidget.action(widgetInfo)
          .then(res => {
            console.log(res);
          })
          .catch(err => {
            console.error("Widget action failed:", err)
          })
    },
    selectionChanged(widgetInfo: WidgetInfo) {
      DashboardWidget.select(widgetInfo)
          .then(res => {
            if (!res) return;

            if (this.widget.widgetType === DashboardWidgetWidgetTypes.MULTITAB) {
              const tabs = this.widget.widgetMeta?.tabs || [];

              const tab = tabs.find(t => t.id === res.id);

              if (tab) {
                tab.data = res.data;
              }
              return;
            }
          })
          .catch(err => {
            console.error("Widget select failed:", err);
          });
    }

  }
})
</script>

<style scoped>
.widget-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 260px;
}
</style>
